import EpiApiClient, { isCancel } from "@/services/serviceClients/epiApiClient";
import {
  SEARCH_INVOICES_API_PATH,
  ORDER_TEMPLATE_V2_DETAILS_BY_ID_API_PATH,
  ORDER_TEMPLATE_DETAILS_BY_IDS_API_PATH,
  ORDER_TEMPLATE_UPDATE_API_PATH,
  SMART_TEMPLATES_API_PATH,
  SMART_TEMPLATES_CONTRACTOR_API_PATH,
  MEASUREMENT_CATEGORIES_API_PATH,
  ADD_ORDER_TEMPLATE_API_PATH,
  ORDER_CATALOG_CATEGORIES_API_PATH,
  ESTIMATOR_SETTINGS_API_PATH,
  GET_ORDER_TEMPLATE_FROM_ORDER_API_PATH,
  GET_ORDER_TEMPLATE_FROM_ORDER_V2_API_PATH,
  GET_ORDER_TEMPLATE_FROM_INVOICE_V2_API_PATH,
  GET_ORDER_TEMPLATE_FROM_INVOICE_API_PATH,
  GET_ORDER_TEMPLATE_FROM_QUOTE_API_PATH,
  DELETE_ORDER_TEMPLATE_API_PATH,
  DUPLICATE_ORDER_TEMPLATE_API_PATH,
  GET_CUSTOMER_DETAILS_API_PATH,
  SEARCH_PRODUCTS_API_PATH
 } from "@/config/constants";

class orderTemplateService {
  static async fetchInvoices({
    pageNum = 1,
    filters = {},
    searchTerm = "",
    orderBy,
    pageSize = 50,
  } = {}) {
    try {
      const payload = {
        skip: (pageNum - 1) * pageSize,
        take: pageSize,
        filter: filters.filterArray || [],
        onlyShowFavorites: filters.favoritesOnly || false,
        searchTerm: searchTerm.length >= 3 ? searchTerm : "",
        orderBy: orderBy || ["InvoiceContent/InvoiceHeader/InvoiceDate desc"],
        UserDate: new Date().toISOString().slice(0, 10).replace(/-/g, ""),
      };

      const { data } = await EpiApiClient.post(
        SEARCH_INVOICES_API_PATH,
        payload
      );

      // Normalize API results
      const results = Array.isArray(data?.Results) ? data.Results : [];

      const invoicesList = results.map((inv) => ({
        number: inv.InvoiceNumber || "",
        type: inv.SOTypeCode || "",
        invoiceDate: inv.InvoiceDate ? inv.InvoiceDate.split("T")[0] : "",
        address: [
          inv.ShipToAddress1,
          inv.ShipToAddress2,
          inv.ShipToCityStateZip,
        ]
          .filter(Boolean)
          .join("\n"),
        po: inv.PONumber || "",
        total:
          inv.InvoiceTotal != null
            ? `$${inv.InvoiceTotal.toLocaleString(undefined, {
                minimumFractionDigits: 2,
              })}`
            : "",
        paymentStatus: inv.PaymentStatus || "",
        dueDate: inv.DueDate ? inv.DueDate.split("T")[0] : "",
        branch: inv.BranchCode || "",
        jobAccount: inv.ShipToSequenceNumber || "",
        manufacturerRewards: inv.ManufacturerRewardPrograms?.length
          ? inv.ManufacturerRewardPrograms.map((r) => r.Name).join(", ")
          : "",
        manufacturerRewardsRaw: inv.ManufacturerRewardPrograms || [],
        statusClass: orderTemplateService.getStatusClass(inv.PaymentStatus),
        orderNumber: inv.OrderNumber || "",
        Latitude: inv.ShipToLatitude,
        Longitude: inv.ShipToLongitude,
        shipToAddress: inv.ShipToAddress1,
        shipToCityStateZip: inv.ShipToCityStateZip,
        invoiceDetails: inv.InvoiceDetails,
      }));

      return {
        success: true,
        data: {
          invoices: invoicesList,
          totalInvoices: data?.DocumentCount || invoicesList.length,
          isLoadMore: data?.DocumentCount > invoicesList.length,
          page: pageNum,
        },
      };
    } catch (error) {
      console.error("fetchInvoices error:", error);
      return {
        success: false,
        error: error?.response?.data?.error_description || "Unknown error",
        data: [],
      };
    }
  }

  // Helper method for UI classes
  static getStatusClass(paymentStatus) {
    switch (paymentStatus?.toLowerCase()) {
      case "paid":
        return "status-paid";
      case "unpaid":
        return "status-unpaid";
      case "overdue":
        return "status-overdue";
      default:
        return "status-unknown";
    }
  }

  static async productSearch(
    searchTerm,
    category,
    branchCode,
    excludeNotAvailableAtBranch,
    skip = 0,
    selectedManufacturers = null,
    includeFrequentItems = true,
    includePromotions = true,
    includeRecommendations = true,
    recommendationsOnly = false,
    selectedListTypes = null,
    productIds = [],
    cancelTokenSource = null
  ) {
    category = category == "ALL" ? "" : category;

    let body = {
      searchTerm: searchTerm,
      branchCode: branchCode,
      excludeNotAvailableAtBranch: excludeNotAvailableAtBranch,
      take: 20,
      skip: skip,
      options: {
        includeAssetTypes: [
          "ProductPrimaryImage",
          "ItemPrimaryImage",
          "ItemAlternateImage",
        ],
        includeFrequentItems: includeFrequentItems,
        includePromotions: includePromotions,
        includeRecommendations: includeRecommendations,
        recommendationsOnly: recommendationsOnly,
        productIds: productIds,
      },
    };

    body.facetFilters = [];

    // Add category filter if category exists
    if (
      category &&
      !(Array.isArray(category) && category.length === 0) &&
      category !== ""
    ) {
      body.facetFilters.push({
      name: "Categories/FullCategoryName",
      values: Array.isArray(category) ? category : [category]
      });
    }

    // Add manufacturer filter if selectedManufacturers is not null or empty
    if (selectedManufacturers !== null && selectedManufacturers.length > 0) {
      body.facetFilters.push({
        name: "Manufacturer",
        values: selectedManufacturers,
      });
    }

    if (
      selectedListTypes !== null &&
      selectedListTypes.length > 0 &&
      selectedListTypes.includes("Generics")
    ) {
      body.facetFilters.push({ name: "IsGeneric", values: [true] });
    }

    if (
      selectedListTypes !== null &&
      selectedListTypes.length > 0 &&
      selectedListTypes.includes("Fortified Eligible")
    ) {
      body.facetFilters.push({ name: "Certifications", values: ["Fortified"] });
    }

    // Set useFacets to true if there are any filters
    if (body.facetFilters.length > 0) {
      body.useFacets = true;
    }

    return EpiApiClient
      .post(SEARCH_PRODUCTS_API_PATH, { ...body, splitSelectableFeatures: true }, {
        headers: {
          "Content-Type": "application/json",
        },
        handleError: true,
        cancelToken: cancelTokenSource,
      })
      .then(function (response) {
        return {
          success: true,
          data: response.data,
        };
      })
      .catch(function (error) {
        if (!isCancel(error)) {
          return {
            success: false,
            error:
              error &&
              error.response &&
              error.response.data &&
              error.response.data.error_description,
            data: {},
          };
        }
      });
  }
  static getOrderTemplateDetailsByIds(templateIds) {
    return EpiApiClient
      .post(ORDER_TEMPLATE_DETAILS_BY_IDS_API_PATH, templateIds)
      .then((response) => ({ success: true, data: response.data }))
      .catch((error) => ({
        success: false,
        error: error.response.data.error_description,
      }));
  }

  static productCategories() {
        return EpiApiClient.get(ORDER_CATALOG_CATEGORIES_API_PATH)
            .then(function (response) {
                return {
                    success: true,
                    data: response.data
                };
            })
            .catch(function (error) {
                if (!isCancel(error)) {
                    return {
                        success: false,
                        error: error && error.response && error.response.data && error.response.data.error_description,
                        data: {}
                    };
                }
            });
    }


  static async getOrderTemplateDetailsById(templateId) {
    return EpiApiClient
      .get(`${ORDER_TEMPLATE_V2_DETAILS_BY_ID_API_PATH}${templateId}`)
      .then((response) => ({ success: true, data: response.data }))
      .catch((error) => ({
        success: false,
        error: error.response.data.error_description,
      }));
  }

  static async updateOrderTemplate(template) {
    return EpiApiClient
      .post(ORDER_TEMPLATE_UPDATE_API_PATH, template)
      .then((response) => ({ success: true, data: response.data }))
      .catch((error) => ({
        success: false,
        error: error.response.data.error_description,
      }));
  }

    static addOrderTemplate(model) {
        return EpiApiClient.post(ADD_ORDER_TEMPLATE_API_PATH,
            model
        )
            .then(function (response) {
                return { success: true, data: response.data };
            }).catch(function (error) {
                return { success: false, error: error.response.data.ModelState };
            });
  }

  static getEstimatorEstimateSettings() {
    return EpiApiClient
      .get(ESTIMATOR_SETTINGS_API_PATH)
      .then((response) => {
        return { success: true, data: response.data };
      })
      .catch(function (error) {
        return { success: false, error: error.response.data.error_description };
      });
  }

  static getMeasurementCategories() {
        return EpiApiClient.get(MEASUREMENT_CATEGORIES_API_PATH)
            .then(function (response) {
          const payload = response.data?.Data ?? response.data;
          return { success: true, data: payload };
            }).catch(function (error) {
                return {
                    success: false, error: error.response.data.error_description, data: {}
                };
            });
  }

  static getOrderTemplateFromOrder(orderNumber, isNewPC = false) {
        let url = isNewPC ? GET_ORDER_TEMPLATE_FROM_ORDER_V2_API_PATH : GET_ORDER_TEMPLATE_FROM_ORDER_API_PATH;
        return EpiApiClient.get(url,
            {
                params: { orderNumber: orderNumber }
            })
            .then(function (response) {
                return { success: true, data: response.data };
            }).catch(function (error) {
                return {
                    success: false, error: error.response.data.error_description, data: {}
                };
            });
    }

    static getOrderTemplateFromInvoice(invoiceNumber, isNewPC = false) {
        let url = isNewPC ? GET_ORDER_TEMPLATE_FROM_INVOICE_V2_API_PATH : GET_ORDER_TEMPLATE_FROM_INVOICE_API_PATH;
        return EpiApiClient.get(url,
            {
                params: { invoiceNumber: invoiceNumber }
            })
            .then(function (response) {
                return { success: true, data: response.data };
            }).catch(function (error) {
                return {
                    success: false, error: error.response.data.error_description, data: {}
                };
            });
    }

    static getOrderTemplateFromQuote(quoteNumber, isPC = false) {
        const endpoint = GET_ORDER_TEMPLATE_FROM_QUOTE_API_PATH;
        return EpiApiClient.get(endpoint,
            {
                params: { quoteNumber: quoteNumber }
            })
            .then(function (response) {
                return { success: true, data: response.data };
            }).catch(function (error) {
                return {
                    success: false, error: error.response.data.error_description, data: {}
                };
            });
    }

    static deleteOrderTemplate(templateId) {
        return EpiApiClient.get(DELETE_ORDER_TEMPLATE_API_PATH,
            {
                params: { templateId: templateId }
            })
            .then(function (response) {
                return { success: true, data: response.data };
            }).catch(function (error) {
                return { success: false, error: error.response.data.error_description };
            });
    }

    static submitCopyTemplate(formData) {
        return EpiApiClient.post(DUPLICATE_ORDER_TEMPLATE_API_PATH, formData)
            .then(function (response) {
                return { success: true, data: response.data };
            }).catch(function (error) {
                return { success: false, error: (error && error.response && error.response.data) };
            });
    }

    static getCustomerDetails(customerCode, handleError) {
        return EpiApiClient.post(GET_CUSTOMER_DETAILS_API_PATH,
            { customerCode: customerCode },
            {
                handleError: handleError
            })
            .then(function (response) {
                return { success: true, data: response.data };
            }).catch(function (error) {
                return {
                    success: false, error: error.response.data.error_description, data: {}
                };
            });
    }
}

export default orderTemplateService;

export const smartTemplateContractorsService = (manufacturer) => ({
  async getSmartTemplateContractors() {
    try {
      const response = await EpiApiClient.get(
        `${SMART_TEMPLATES_CONTRACTOR_API_PATH}${manufacturer}`
      );

      const smartTemplateContractors =
        response.data?.Data?.CertificationLevels || [];
      return { success: true, data: smartTemplateContractors };
    } catch (error) {
      console.error("SmartTemplateContractorsService Error:", error);
      return { success: false, error: error.message };
    }
  },
});

export const smartTemplateService = {
  async getSmartTemplates(payload) {
    try {
      const response = await EpiApiClient.post(SMART_TEMPLATES_API_PATH, payload);
      const smartTemplates = response.data?.SmartTemplates || [];
      return { success: true, data: smartTemplates };
    } catch (error) {
      console.error("SmartTemplateService Error:", error);
      return { success: false, error: error.message };
    }
  },
};

export const measurementService = {
  async getMeasurementCategories() {
    try {
      const response = await EpiApiClient.get(MEASUREMENT_CATEGORIES_API_PATH);
      const measurementCategories = response.data ?? null;
      return { success: true, data: measurementCategories };
    } catch (error) {
      console.error("MeasurementService Error:", error);
      return { success: false, error: error.message };
    }
  },
};